import styled from 'styled-components';
import { SpaceProps, space } from 'styled-system';

// Accepted sizes for <Loader /> size prop
export type LoaderSizeType = 'sm' | 'md' | 'lg';

export interface ILoaderProps {
  // Determines the height/width that <Loader /> is rendered with
  size?: LoaderSizeType;
}

export type StyleLoaderProps = SpaceProps & ILoaderProps;

// Default dimensions/font sizes for different <Loader /> sizes
const LOADER_CONFIG = {
  sm: {
    size: '16px',
    font: '10px'
  },
  md: {
    size: '32px',
    font: '12px'
  },
  lg: {
    size: '64px',
    font: '16px'
  }
};

/**
 * Returns correct dimensions based on size of <Loader />
 *
 * @param {string} loaderSize
 * @returns {string}
 */
const setLoaderSize = (loaderSize: string): string => {
  switch (loaderSize) {
    case 'sm':
      return LOADER_CONFIG[loaderSize].size;
    case 'md':
      return LOADER_CONFIG[loaderSize].size;
    case 'lg':
      return LOADER_CONFIG[loaderSize].size;
    default:
      return LOADER_CONFIG[loaderSize].size;
  }
};

/**
 * Returns main loader circle positioning for IE browsers
 * @param loaderSize
 */
const setIEOffset = (loaderSize: string): string => {
  switch (loaderSize) {
    case 'sm':
      return '12px';
    case 'md':
      return '14px';
    case 'lg':
      return '8px';
    default:
      return '12px';
  }
};

/**
 * Returns corrent font size for <Loader /> caption
 *
 * @param {string} fontSize
 * @returns {string}
 */
const setLoaderTextFontSize = (fontSize: string): string => {
  switch (fontSize) {
    case 'sm':
      return LOADER_CONFIG.sm.font;
    case 'md':
      return LOADER_CONFIG.md.font;
    case 'lg':
      return LOADER_CONFIG.lg.font;
    default:
      return LOADER_CONFIG.sm.font;
  }
};

export const LoaderWrapper = styled.div`
  ${space}
`;

export const Circle = styled.div<StyleLoaderProps>`
  display: block;
  position: relative;
  box-sizing: border-box;
  font-size: 0;
  color: #333;
  > div {
    position: relative;
    box-sizing: border-box;
    display: inline-block;
    float: none;
    background-color: currentColor;
    border: 0 solid currentColor;
  }
  > div:nth-child(1) {
    position: absolute;
    top: 50%;
    left: 50%;
    \\ Targets IE11 browsers to manually align loader components
    @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
      top: ${({ size }) =>
        size && -parseInt(setLoaderSize(size).substring(0, 2), 10) / 2}px;
      left: ${({ size }) => size && setIEOffset(size)};
    }
    z-index: 1;
    width: ${({ size }) => size && setLoaderSize(size)};
    height: ${({ size }) => size && setLoaderSize(size)};
    background: #aaa;
    border-radius: 100%;
    -webkit-transform: translate(-50%, -50%);
    -moz-transform: translate(-50%, -50%);
    -o-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
    -webkit-animation: ball-atom-shrink 4.5s infinite linear;
    -moz-animation: ball-atom-shrink 4.5s infinite linear;
    -o-animation: ball-atom-shrink 4.5s infinite linear;
    animation: ball-atom-shrink 4.5s infinite linear;
  }
  > div:not(:nth-child(1)) {
    position: absolute;
    left: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    background: none;
    -webkit-animation: ball-atom-zindex 1.5s 0s infinite steps(2, end);
    -moz-animation: ball-atom-zindex 1.5s 0s infinite steps(2, end);
    -o-animation: ball-atom-zindex 1.5s 0s infinite steps(2, end);
    animation: ball-atom-zindex 1.5s 0s infinite steps(2, end);
  }
  > div:not(:nth-child(1)):before {
    position: absolute;
    top: 0;
    left: 0;
    width: 10px;
    height: 10px;
    margin-top: -5px;
    margin-left: -5px;
    content: '';
    background: currentColor;
    border-radius: 50%;
    opacity: 0.75;
    -webkit-animation: ball-atom-position 1.5s 0s infinite ease,
      ball-atom-size 1.5s 0s infinite ease;
    -moz-animation: ball-atom-position 1.5s 0s infinite ease,
      ball-atom-size 1.5s 0s infinite ease;
    -o-animation: ball-atom-position 1.5s 0s infinite ease,
      ball-atom-size 1.5s 0s infinite ease;
    animation: ball-atom-position 1.5s 0s infinite ease,
      ball-atom-size 1.5s 0s infinite ease;
  }
  > div:nth-child(2) {
    -webkit-animation-delay: 0.75s;
    -moz-animation-delay: 0.75s;
    -o-animation-delay: 0.75s;
    animation-delay: 0.75s;
  }
  > div:nth-child(2):before {
    -webkit-animation-delay: 0s, -1.125s;
    -moz-animation-delay: 0s, -1.125s;
    -o-animation-delay: 0s, -1.125s;
    animation-delay: 0s, -1.125s;
  }
  > div:nth-child(3) {
    -webkit-transform: rotate(120deg);
    -moz-transform: rotate(120deg);
    -o-transform: rotate(120deg);
    transform: rotate(120deg);
    -webkit-animation-delay: -0.25s;
    -moz-animation-delay: -0.25s;
    -o-animation-delay: -0.25s;
    animation-delay: -0.25s;
  }
  > div:nth-child(3):before {
    -webkit-animation-delay: -1s, -0.75s;
    -moz-animation-delay: -1s, -0.75s;
    -o-animation-delay: -1s, -0.75s;
    animation-delay: -1s, -0.75s;
  }
  > div:nth-child(4) {
    -webkit-transform: rotate(240deg);
    -moz-transform: rotate(240deg);
    -o-transform: rotate(240deg);
    transform: rotate(240deg);
    -webkit-animation-delay: 0.25s;
    -moz-animation-delay: 0.25s;
    -o-animation-delay: 0.25s;
    animation-delay: 0.25s;
  }
  > div:nth-child(4):before {
    -webkit-animation-delay: -0.5s, -0.125s;
    -moz-animation-delay: -0.5s, -0.125s;
    -o-animation-delay: -0.5s, -0.125s;
    animation-delay: -0.5s, -0.125s;
  }
  @-webkit-keyframes ball-atom-position {
    50% {
      top: 100%;
      left: 100%;
    }
  }
  @-moz-keyframes ball-atom-position {
    50% {
      top: 100%;
      left: 100%;
    }
  }
  @-o-keyframes ball-atom-position {
    50% {
      top: 100%;
      left: 100%;
    }
  }
  @keyframes ball-atom-position {
    50% {
      top: 100%;
      left: 100%;
    }
  }
  @-webkit-keyframes ball-atom-size {
    50% {
      -webkit-transform: scale(0.5, 0.5);
      transform: scale(0.5, 0.5);
    }
  }
  @-moz-keyframes ball-atom-size {
    50% {
      -moz-transform: scale(0.5, 0.5);
      transform: scale(0.5, 0.5);
    }
  }
  @-o-keyframes ball-atom-size {
    50% {
      -o-transform: scale(0.5, 0.5);
      transform: scale(0.5, 0.5);
    }
  }
  @keyframes ball-atom-size {
    50% {
      -webkit-transform: scale(0.5, 0.5);
      -moz-transform: scale(0.5, 0.5);
      -o-transform: scale(0.5, 0.5);
      transform: scale(0.5, 0.5);
    }
  }
  @-webkit-keyframes ball-atom-zindex {
    50% {
      z-index: 10;
    }
  }
  @-moz-keyframes ball-atom-zindex {
    50% {
      z-index: 10;
    }
  }
  @-o-keyframes ball-atom-zindex {
    50% {
      z-index: 10;
    }
  }
  @keyframes ball-atom-zindex {
    50% {
      z-index: 10;
    }
  }
  @-webkit-keyframes ball-atom-shrink {
    50% {
      -webkit-transform: translate(-50%, -50%) scale(0.8, 0.8);
      transform: translate(-50%, -50%) scale(0.8, 0.8);
    }
  }
  @-moz-keyframes ball-atom-shrink {
    50% {
      -moz-transform: translate(-50%, -50%) scale(0.8, 0.8);
      transform: translate(-50%, -50%) scale(0.8, 0.8);
    }
  }
  @-o-keyframes ball-atom-shrink {
    50% {
      -o-transform: translate(-50%, -50%) scale(0.8, 0.8);
      transform: translate(-50%, -50%) scale(0.8, 0.8);
    }
  }
  @keyframes ball-atom-shrink {
    50% {
      -webkit-transform: translate(-50%, -50%) scale(0.8, 0.8);
      -moz-transform: translate(-50%, -50%) scale(0.8, 0.8);
      -o-transform: translate(-50%, -50%) scale(0.8, 0.8);
      transform: translate(-50%, -50%) scale(0.8, 0.8);
    }
  }
`;

export const LoaderText = styled.p<StyleLoaderProps>`
  font-size: ${({ size }) => size && setLoaderTextFontSize(size)};
`;
